import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, Wrench, BadgeCheck, Store, Sparkles } from "lucide-react";
import { api, inr } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

const HERO = "https://images.unsplash.com/photo-1758448500717-2e4bcd79108b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function Section({ title, subtitle, children, action, id, dark }) {
  return (
    <section id={id} className={`mx-auto max-w-[1400px] px-4 md:px-8 py-14 md:py-20 ${dark ? "text-white" : ""}`}>
      <Reveal>
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            {subtitle && <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">{subtitle}</span>}
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold">{title}</h2>
          </div>
          {action}
        </div>
      </Reveal>
      {children}
    </section>
  );
}

function Row({ products }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
      {products.map((p, i) => (
        <Reveal key={p.id} delay={(i % 5) * 0.05}><ProductCard product={p} /></Reveal>
      ))}
    </div>
  );
}

export default function Home() {
  const [d, setD] = useState({ marts: [], featured: [], trending: [], newArr: [], offers: [], signature: [], vendors: [], stores: [], blog: [], branded: [], home: [], style: [], tech: [] });

  useEffect(() => {
    const g = (params) => api.get(`/products?${params}`).then((r) => r.data.items).catch(() => []);
    Promise.all([
      api.get("/marts").then((r) => r.data),
      g("is_featured=true&limit=10"),
      g("is_trending=true&limit=10"),
      g("is_new=true&limit=10"),
      api.get("/offers").then((r) => r.data),
      g("product_type=signature"),
      api.get("/vendors").then((r) => r.data),
      api.get("/stores").then((r) => r.data),
      api.get("/blog").then((r) => r.data),
      g("product_type=branded_furniture&limit=8"),
      g("mart=home-mart&limit=5"),
      g("mart=style-mart&limit=5"),
      g("mart=tech-mart&limit=5"),
    ]).then(([marts, featured, trending, newArr, offers, signature, vendors, stores, blog, branded, home, style, tech]) =>
      setD({ marts, featured, trending, newArr, offers, signature, vendors, stores, blog, branded, home, style, tech }));
  }, []);

  const martImg = (slug) => d.marts.find((m) => m.slug === slug)?.image;

  return (
    <div>
      {/* HERO */}
      <div className="px-3 md:px-6 pt-4">
        <div className="relative overflow-hidden rounded-3xl">
          <img src={HERO} alt="KRVMart retail experience" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight/90 via-midnight/70 to-midnight/20" />
          <div className="relative px-6 md:px-16 py-20 md:py-32 max-w-3xl">
            <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold text-white">
              <Sparkles className="h-3.5 w-3.5 text-gold" /> A retail brand of KRV Nexus Pvt Ltd
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05]">
              Everything for<br /><span className="text-gold">Everyday Living.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-5 text-base md:text-lg text-white/80 max-w-xl">
              Discover groceries, fashion, furniture, technology, and everyday essentials — thoughtfully brought together under one modern retail experience.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-now" className="rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-midnight hover:bg-gold-soft transition-colors">Shop Now</Link>
              <a href="#explore-marts" data-testid="hero-explore-marts" className="rounded-full border border-white/30 bg-white/5 backdrop-blur px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">Explore Our Marts</a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* FOUR MARTS - bento */}
      <Section id="explore-marts" title="Explore KRVMart" subtitle="Four destinations. One experience.">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {d.marts.map((m, i) => {
            const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
            return (
              <Reveal key={m.slug} delay={i * 0.08} className={spans[i]}>
                <Link to={`/${m.slug}`} data-testid={`mart-card-${m.slug}`} className="group relative block h-72 md:h-80 overflow-hidden rounded-3xl">
                  <img src={m.image} alt={m.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-midnight/90 via-midnight/40 to-transparent" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-end">
                    <span className="mb-2 h-1.5 w-14 rounded-full" style={{ background: m.color }} />
                    <h3 className="text-2xl font-extrabold text-white">{m.name}</h3>
                    <p className="text-xs font-semibold uppercase tracking-wide text-white/70">{m.tagline}</p>
                    <p className="mt-2 max-w-md text-sm text-white/70 line-clamp-2">{m.description}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-gold">Shop {m.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* FEATURED */}
      {d.featured.length > 0 && (
        <Section title="Featured Products" subtitle="Handpicked for you"
          action={<Link to="/shop" className="text-sm font-bold text-gold-dark hover:underline">View all</Link>}>
          <Row products={d.featured.slice(0, 5)} />
        </Section>
      )}

      {/* TRENDING */}
      {d.trending.length > 0 && (
        <div className="bg-secondary">
          <Section title="Trending Now" subtitle="What everyone's buying">
            <Row products={d.trending.slice(0, 5)} />
          </Section>
        </div>
      )}

      {/* OFFERS */}
      <Section title="Offers" subtitle="Real value, no fake discounts"
        action={<Link to="/offers" className="text-sm font-bold text-gold-dark hover:underline">All offers</Link>}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          {d.offers.slice(0, 3).map((o, i) => (
            <Reveal key={o.slug} delay={i * 0.08}>
              <Link to={`/offers?type=${encodeURIComponent(o.slug)}`} className="group relative block h-48 overflow-hidden rounded-3xl">
                <img src={o.banner} alt={o.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-midnight/50" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <h3 className="text-xl font-extrabold text-white">{o.title}</h3>
                  <p className="text-sm text-white/70 line-clamp-2">{o.description}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* HOME / STYLE MART strips */}
      {["home-mart", "style-mart"].map((slug) => {
        const m = d.marts.find((x) => x.slug === slug);
        const items = slug === "home-mart" ? d.home : d.style;
        if (!m || items.length === 0) return null;
        return (
          <Section key={slug} title={m.name} subtitle={m.tagline}
            action={<Link to={`/${slug}`} className="text-sm font-bold text-gold-dark hover:underline">Shop {m.name}</Link>}>
            <Row products={items} />
          </Section>
        );
      })}

      {/* WOOD MART + SIGNATURE (dark) */}
      <div className="bg-midnight">
        <Section title="Wood Mart" subtitle="Furniture • Home Furnishing" dark
          action={<Link to="/wood-mart" className="text-sm font-bold text-gold hover:underline">Explore Wood Mart</Link>}>
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <Reveal>
              <img src={martImg("wood-mart")} alt="Wood Mart" className="h-80 w-full rounded-3xl object-cover" />
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="text-2xl md:text-3xl font-extrabold text-white">A hybrid furniture experience</h3>
              <p className="mt-3 text-white/70">Branded furniture, curated local-vendor collections, made-to-order customization, and the evolving Wood Mart Signature Collection — all under the KRVMart promise of quality and installation service.</p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[["Branded Furniture", "/wood-mart?type=branded"], ["Vendor Collections", "/vendors"], ["Signature Collection", "/wood-mart?type=signature"], ["Custom Orders", "/wood-mart"]].map(([l, to]) => (
                  <Link key={l} to={to} className="rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors">{l}</Link>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Signature */}
          <div className="mt-14">
            <div className="mb-6 flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-gold" />
              <div>
                <h3 className="text-2xl font-extrabold text-white">Wood Mart Signature Collection</h3>
                <p className="text-sm text-white/60">Designed for Modern Urban Living.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {d.signature.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}><ProductCard product={p} /></Reveal>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* TECH MART */}
      {d.tech.length > 0 && (
        <Section title="Tech Mart" subtitle="Electronics • Appliances • Gadgets"
          action={<Link to="/tech-mart" className="text-sm font-bold text-gold-dark hover:underline">Shop Tech Mart</Link>}>
          <Row products={d.tech} />
        </Section>
      )}

      {/* BRANDED PRODUCTS */}
      {d.branded.length > 0 && (
        <div className="bg-secondary">
          <Section title="Branded Furniture" subtitle="Trusted names">
            <Row products={d.branded.slice(0, 5)} />
          </Section>
        </div>
      )}

      {/* VENDOR COLLECTIONS */}
      <Section title="Vendor Collections" subtitle="Curated local craftsmanship"
        action={<Link to="/vendors" className="text-sm font-bold text-gold-dark hover:underline">All vendors</Link>}>
        <div className="grid md:grid-cols-2 gap-6">
          {d.vendors.map((v, i) => (
            <Reveal key={v.vendor_id} delay={i * 0.08}>
              <Link to={`/vendors/${v.vendor_id}`} className="group flex gap-5 rounded-3xl border border-border bg-card p-5 transition-shadow hover:shadow-lg">
                <img src={v.image} alt={v.name} className="h-28 w-28 rounded-2xl object-cover" />
                <div className="flex-1">
                  <h3 className="text-lg font-extrabold text-midnight">{v.name}</h3>
                  <p className="text-xs font-semibold text-mart-wood">{v.city} • ★ {v.rating}</p>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{v.profile}</p>
                  <p className="mt-2 text-xs font-semibold text-midnight">{v.price_range}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* STORE EXPERIENCE */}
      <div className="bg-midnight">
        <Section title="A Modern Retail Destination" subtitle="More than shopping" dark>
          <p className="max-w-2xl text-white/70 -mt-4 mb-8">Our planned flagship store brings all four marts together across floors — organized, category-based discovery in one premium space. <span className="text-gold">(Planned Flagship Store Concept)</span></p>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { f: "Ground Floor", t: "Home Mart", d: "Grocery + Personal Care + Home Essentials" },
              { f: "First Floor", t: "Style Mart", d: "Fashion + Imitation Jewellery" },
              { f: "Second Floor", t: "Wood Mart + Tech Mart", d: "Furniture + Electronics" },
            ].map((s, i) => (
              <Reveal key={s.f} delay={i * 0.08}>
                <div className="rounded-3xl border border-white/10 bg-white/5 p-7">
                  <span className="text-xs font-bold uppercase tracking-widest text-gold">{s.f}</span>
                  <h3 className="mt-2 text-xl font-extrabold text-white">{s.t}</h3>
                  <p className="mt-1 text-sm text-white/60">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[[Truck, "Home Delivery"], [Wrench, "Furniture Installation"], [Store, "Click & Collect (planned)"], [BadgeCheck, "Genuine Branded Products"]].map(([Icon, l], i) => (
              <Reveal key={l} delay={i * 0.06}>
                <div className="flex items-center gap-3 rounded-2xl border border-white/10 p-4">
                  <Icon className="h-5 w-5 text-gold" /><span className="text-sm font-semibold text-white">{l}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* STORES */}
      <Section title="Find a KRVMart Store" subtitle="Multi-city, by design"
        action={<Link to="/stores" className="text-sm font-bold text-gold-dark hover:underline">All stores</Link>}>
        <div className="grid md:grid-cols-3 gap-5">
          {d.stores.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <Link to={`/stores/${s.slug}`} className="block rounded-3xl border border-border bg-card p-6 transition-shadow hover:shadow-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-extrabold text-midnight">{s.name}</h3>
                  <span className="rounded-full bg-gold/15 px-3 py-1 text-[11px] font-bold uppercase text-gold-dark">{s.status}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{s.address}</p>
                <p className="mt-3 text-xs font-semibold text-midnight">{s.marts.join(" • ")}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* JOURNAL */}
      <div className="bg-secondary">
        <Section title="KRVMart Journal" subtitle="Guides & inspiration"
          action={<Link to="/blog" className="text-sm font-bold text-gold-dark hover:underline">Read more</Link>}>
          <div className="grid md:grid-cols-4 gap-5">
            {d.blog.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.06}>
                <Link to={`/blog/${b.slug}`} className="group block overflow-hidden rounded-3xl border border-border bg-card">
                  <div className="aspect-video overflow-hidden"><img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                  <div className="p-5">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-gold-dark">{b.category}</span>
                    <h3 className="mt-1 text-sm font-bold text-midnight line-clamp-2">{b.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* VENDOR / FRANCHISE CTA */}
      <Section title="Grow With KRVMart" subtitle="Partnerships & expansion">
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { t: "Become a Vendor", d: "Partner with KRVMart as a manufacturer, supplier, carpenter or brand.", to: "/become-vendor", img: "https://images.unsplash.com/photo-1633505899118-4ca6bd143043?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
            { t: "Franchise With KRVMart", d: "A scalable multi-category retail concept designed to expand across cities.", to: "/franchise", img: "https://images.unsplash.com/photo-1758448500717-2e4bcd79108b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.08}>
              <Link to={c.to} className="group relative block h-56 overflow-hidden rounded-3xl">
                <img src={c.img} alt={c.t} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-r from-midnight/90 to-midnight/30" />
                <div className="absolute inset-0 p-8 flex flex-col justify-center max-w-sm">
                  <h3 className="text-2xl font-extrabold text-white">{c.t}</h3>
                  <p className="mt-2 text-sm text-white/70">{c.d}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-gold">Register Interest <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>
    </div>
  );
}
